import React, { useEffect, useState } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';

interface TenantData {
  id: number;
  name: string;
  plan: string;
  email_notification?: string;
  address?: string;
  created_at?: string;
}

const TenantSettings: React.FC = () => {
  const { token, user } = useAuth();
  const [tenant, setTenant] = useState<TenantData | null>(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    email_notification: '',
    address: ''
  });

  useEffect(() => {
    const fetchTenant = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // If user has a tenant, try to fetch its settings
        if (user?.tenant_id) {
          const response = await fetch(`${API_URL}/api/tenant/settings`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.ok) {
            const data = await response.json();
            setTenant(data);
            setFormData({
              email_notification: data.email_notification || '',
              address: data.address || ''
            });
          } else {
            // Fallback: create mock tenant from user info
            setTenant({
              id: user.tenant_id,
              name: 'Mon Organisation',
              plan: 'standard'
            });
          }
        } else {
          // No tenant - show info message
          setTenant(null);
        }
      } catch (error) {
        console.error('Error fetching tenant:', error);
        // Fallback on error
        if (user?.tenant_id) {
          setTenant({
            id: user.tenant_id,
            name: 'Mon Organisation',
            plan: 'standard'
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTenant();
  }, [token, user]);

  const handleSave = async () => {
    alert('Paramètres sauvegardés (fonctionnalité en cours de développement)');
  };

  return (
    <SiteLayout>
      <Header icon='settings' title='Paramètres du Tenant' />
      <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
        {loading ? (
          <Box>
            <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
              <div style={{ fontSize: '24px', marginBottom: '12px' }}>⏳</div>
              Chargement des paramètres...
            </div>
          </Box>
        ) : !tenant ? (
          <Box>
            <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏢</div>
              <div style={{ fontSize: '16px', marginBottom: '8px', color: '#475569' }}>
                Aucun tenant associé
              </div>
              <div style={{ fontSize: '14px' }}>
                Contactez l'administrateur pour rejoindre une organisation.
              </div>
            </div>
          </Box>
        ) : (
          <>
            {/* Tenant Info Card */}
            <Box>
              <div style={{ padding: '24px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  marginBottom: '24px',
                  paddingBottom: '20px',
                  borderBottom: '1px solid #e2e8f0'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    color: 'white'
                  }}>
                    🏢
                  </div>
                  <div>
                    <h2 style={{ margin: 0, color: '#1e293b', fontSize: '20px' }}>
                      {tenant.name}
                    </h2>
                    <span style={{
                      display: 'inline-block',
                      marginTop: '6px',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 600,
                      backgroundColor: tenant.plan === 'premium' ? '#fef3c7' : '#e0f2fe',
                      color: tenant.plan === 'premium' ? '#d97706' : '#0369a1',
                      textTransform: 'capitalize'
                    }}>
                      Plan {tenant.plan || 'Standard'}
                    </span>
                  </div>
                </div>

                {/* Settings Form */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                  {/* Organization Name (read-only) */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#475569',
                      marginBottom: '8px'
                    }}>
                      Nom de l'organisation
                    </label>
                    <input
                      type='text'
                      value={tenant.name}
                      disabled
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        backgroundColor: '#f8fafc',
                        color: '#64748b',
                        fontSize: '14px'
                      }}
                    />
                    <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '6px' }}>
                      Contactez le support pour modifier le nom de l'organisation.
                    </p>
                  </div>

                  {/* Notification Email */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#475569',
                      marginBottom: '8px'
                    }}>
                      Email de notification
                    </label>
                    <input
                      type='email'
                      placeholder='admin@example.com'
                      value={formData.email_notification}
                      onChange={(e) => setFormData({ ...formData, email_notification: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        fontSize: '14px',
                        color: '#1e293b'
                      }}
                    />
                  </div>

                  {/* Address */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#475569',
                      marginBottom: '8px'
                    }}>
                      Adresse
                    </label>
                    <input
                      type='text'
                      placeholder='Abidjan, Cocody'
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        fontSize: '14px',
                        color: '#1e293b'
                      }}
                    />
                  </div>
                </div>

                {/* Save Button */}
                <div style={{ marginTop: '30px' }}>
                  <button
                    onClick={handleSave}
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    💾 Enregistrer les modifications
                  </button>
                </div>
              </div>
            </Box>

            {/* Plan Info */}
            <Box>
              <div style={{ padding: '20px' }}>
                <h3 style={{ margin: '0 0 16px 0', color: '#1e293b', fontSize: '16px' }}>
                  📊 Informations sur le plan
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '16px'
                }}>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#667eea' }}>
                      {tenant.plan === 'premium' ? '∞' : '5'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>Utilisateurs max</div>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#667eea' }}>
                      {tenant.plan === 'premium' ? '✓' : '✗'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>API Access</div>
                  </div>
                </div>
                {tenant.plan !== 'premium' && (
                  <button
                    style={{
                      width: '100%',
                      marginTop: '16px',
                      padding: '12px',
                      backgroundColor: '#fef3c7',
                      color: '#d97706',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                    onClick={() => alert('Contactez-nous pour passer au plan Premium!')}
                  >
                    ⭐ Passer au plan Premium
                  </button>
                )}
              </div>
            </Box>
          </>
        )}
      </div>
    </SiteLayout>
  );
};

export default TenantSettings;
